import { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardContent, Skeleton, Typography, Box } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { activityApi } from '@/api/activityApi'
import { ROUTES } from '@/utils/constants'
import type { ActivityType } from '@/types/activity'

const SELF_PRACTICE_TYPE: ActivityType = 'exercise.trained'
const WINDOW_DAYS = 30
const PAGE_SIZE = 100

/** Дата в локальной таймзоне: toISOString() отдал бы UTC-сутки. */
const localDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

interface StudentSelfPracticeCardProps {
  studentId: number
}

/**
 * Самостоятельные тренировки ученика в карточке учителя.
 *
 * Показываем факт и объём — сколько заданий ученик перерешал сам за месяц, — а
 * не разбор попыток: тренировка не оценка, и покадровый просмотр чужих
 * черновиков ученику ощущался бы слежкой. Ссылка ведёт в журнал активности,
 * где события лежат по дням.
 *
 * В средний балл темы и очередь проверки эти работы не входят — инвариант
 * держится на бэкенде (ExerciseScoreReader, ReviewQueueService).
 */
export function StudentSelfPracticeCard({ studentId }: StudentSelfPracticeCardProps) {
  const { t } = useTranslation()
  const [count, setCount] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const to = new Date()
      const from = new Date()
      from.setDate(to.getDate() - WINDOW_DAYS)

      const response = await activityApi.feed({
        userId: studentId,
        types: [SELF_PRACTICE_TYPE],
        // Локальная дата, а не UTC: период на бэкенде считается в таймзоне
        // приложения, и toISOString ночью отдавал бы вчерашнюю границу,
        // отрезая сегодняшние тренировки.
        from: localDate(from),
        to: localDate(to),
        perPage: PAGE_SIZE,
      })

      setCount(response.groups.reduce((sum, group) => sum + group.count, 0))
      // Курсор не листаем: карточке нужен порядок величины, а не точное
      // число. Но упереться в страницу и молча показать «ровно 100» нельзя.
      setHasMore(response.has_more)
    } catch {
      // Блок вспомогательный: ошибка загрузки не должна ломать карточку
      // ученика, ради которой сюда и заходят.
      setCount(null)
    } finally {
      setLoading(false)
    }
  }, [studentId])

  useEffect(() => {
    void load()
  }, [load])

  if (!loading && count === null) return null

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('activity.selfPractice.title')}
        </Typography>

        {loading ? (
          <Skeleton variant="text" width={220} height={28} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {count
                ? hasMore
                  ? t('activity.selfPractice.summaryMore', { count, days: WINDOW_DAYS })
                  : t('activity.selfPractice.summary', { count, days: WINDOW_DAYS })
                : t('activity.selfPractice.empty', { days: WINDOW_DAYS })}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={ROUTES.USER_ACTIVITY(studentId)}
              sx={{ textTransform: 'none' }}
            >
              {t('activity.selfPractice.openJournal')}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
